// Global fetch interceptor.
//
// When an authenticated API call fails with 401/403 (expired/invalid access
// token), this transparently uses the stored refresh token to obtain a new
// access token and retries the original request once. This lets a
// continuously-running session keep working without the user ever having to
// log out and back in.
//
// Installed as a side-effect import from src/index.js (before <App/> renders),
// so every existing `fetch(...)` call across all pages benefits automatically.

const originalFetch = window.fetch.bind(window);

// Shared in-flight refresh so that many simultaneous 401s trigger only ONE
// refresh round-trip (the backend uses rolling refresh tokens — concurrent
// refreshes would invalidate each other).
let refreshPromise = null;

function getRefreshToken() {
  return localStorage.getItem('refreshToken');
}

// Only intercept our own API calls. Skips the print service (:5001) and the
// auth endpoints themselves (login / refresh) to avoid recursion.
function isApiRequest(url) {
  return typeof url === 'string' && url.includes('/api/') && !url.includes('/api/auth/');
}

async function doRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('no-refresh-token');

  const res = await originalFetch('/api/auth/refresh-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) throw new Error('refresh-failed');

  const data = await res.json();
  localStorage.setItem('token', data.tokens.access.token);
  localStorage.setItem('refreshToken', data.tokens.refresh.token);
  if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
  return data.tokens.access.token;
}

function refreshOnce() {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function withAuthHeader(init, token) {
  const headers = new Headers((init && init.headers) || {});
  headers.set('Authorization', `Bearer ${token}`);
  return { ...(init || {}), headers };
}

function forceLogout() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  if (!window.location.pathname.includes('/login')) {
    window.location.assign('/login');
  }
}

window.fetch = async (input, init) => {
  const url = typeof input === 'string' ? input : input && input.url;

  const response = await originalFetch(input, init);

  if (
    (response.status === 401 || response.status === 403) &&
    isApiRequest(url) &&
    getRefreshToken()
  ) {
    try {
      const newToken = await refreshOnce();
      // Retry once with the fresh token. Uses originalFetch so the retry does
      // not loop back through this interceptor.
      return await originalFetch(input, withAuthHeader(init, newToken));
    } catch {
      // Refresh token is gone/expired/revoked → the session is truly over.
      forceLogout();
      return response;
    }
  }

  return response;
};
